import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import TestimonialCard from "./TestimonialCard";
import testimonialsData from "../../JsonFiles/Testimonial.json";
import "./Testimonial.css";

const AUTOPLAY_DELAY = 6500;

const Testimonial = ({ id = "testimonials" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const totalTestimonials = testimonialsData.length;

  useEffect(() => {
    const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      setPrefersReducedMotion(motionPreference.matches);
    };

    updateMotionPreference();
    motionPreference.addEventListener?.("change", updateMotionPreference);

    return () => {
      motionPreference.removeEventListener?.("change", updateMotionPreference);
    };
  }, []);

  useEffect(() => {
    if (isPaused || prefersReducedMotion || totalTestimonials <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % totalTestimonials);
    }, AUTOPLAY_DELAY);

    return () => window.clearInterval(timer);
  }, [isPaused, prefersReducedMotion, totalTestimonials]);

  if (totalTestimonials === 0) return null;

  const showPrevious = () => {
    setCurrentIndex((previous) =>
      previous === 0 ? totalTestimonials - 1 : previous - 1,
    );
  };

  const showNext = () => {
    setCurrentIndex((previous) => (previous + 1) % totalTestimonials);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPrevious();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      showNext();
    }
  };

  return (
    <section
      id={id}
      className="testimonial-section"
      aria-labelledby="testimonial-title"
    >
      <div className="testimonial-section__decoration" aria-hidden="true" />

      <div className="testimonial-section__container">
        <header className="testimonial-section__header">
          <div>
            <p className="testimonial-section__eyebrow">
              <span aria-hidden="true" />
              Client stories
            </p>

            <h2 id="testimonial-title" className="testimonial-section__title">
              Trusted partnerships.
              <span> Meaningful results.</span>
            </h2>
          </div>

          <div className="testimonial-section__header-side">
            <p>
              The strongest measure of our work is the confidence our clients
              place in us—and the value we continue to create together.
            </p>

            {totalTestimonials > 1 && (
              <div className="testimonial-section__controls">
                <button
                  type="button"
                  onClick={showPrevious}
                  aria-label="Show previous testimonial"
                >
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                </button>

                <button
                  type="button"
                  onClick={showNext}
                  aria-label="Show next testimonial"
                >
                  <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </header>

        <div
          className="testimonial-section__carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label="Client testimonials"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocusCapture={() => setIsPaused(true)}
          onBlurCapture={() => setIsPaused(false)}
        >
          <div
            className="testimonial-section__track"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {testimonialsData.map((testimonial, index) => (
              <div
                key={testimonial.id ?? testimonial.name ?? index}
                className="testimonial-section__slide"
                aria-hidden={index !== currentIndex}
              >
                <TestimonialCard
                  testimonial={testimonial}
                  sequence={index + 1}
                  isActive={index === currentIndex}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="testimonial-section__footer">
          <p className="testimonial-section__counter" aria-live="polite">
            <strong>{String(currentIndex + 1).padStart(2, "0")}</strong>
            <span aria-hidden="true" />
            {String(totalTestimonials).padStart(2, "0")}
          </p>

          {totalTestimonials > 1 && (
            <div className="testimonial-section__pagination" aria-label="Select testimonial">
              {testimonialsData.map((testimonial, index) => (
                <button
                  key={testimonial.id ?? testimonial.name ?? index}
                  type="button"
                  className={index === currentIndex ? "is-active" : ""}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Show testimonial ${index + 1}`}
                  aria-current={index === currentIndex ? "true" : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
